import React, { FC, ReactNode, useState } from 'react';
import { Menu, MenuItem, Box } from '@mui/material';

export const menuIcon = (inner: string, size = 17, sw = 1.7) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{ __html: inner }}
  />
);

export const KEBAB_GLYPH = menuIcon(
  '<circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none"/>'
);

export const MENU_ICONS = {
  view: menuIcon('<path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="3"/>'),
  share: menuIcon('<path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/><path d="M12 16V3m0 0L8 7m4-4 4 4"/>'),
  addPerson: menuIcon('<path d="M16 20v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V20"/><circle cx="9.5" cy="7.5" r="3.5"/><path d="M18 7.5v6M21 10.5h-6"/>'),
  project: menuIcon('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/><path d="M9 5v5"/>'),
  edit: menuIcon('<path d="m14.5 5.5 4 4L8 20H4v-4z"/><path d="m12.5 7.5 4 4"/>'),
  duplicate: menuIcon('<rect x="9" y="9" width="12" height="12" rx="2"/><path d="M15 5.5A2.5 2.5 0 0 0 12.5 3H5.5A2.5 2.5 0 0 0 3 5.5v7A2.5 2.5 0 0 0 5.5 15"/>'),
  sku: menuIcon('<path d="m12 3 9 5-9 5-9-5z"/><path d="m3 12 9 5 9-5"/><path d="m3 16 9 5 9-5"/>'),
  settings: menuIcon('<circle cx="12" cy="12" r="3.2"/><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z"/>'),
  trash: menuIcon('<path d="M4 7h16"/><path d="M9 7V4.5h6V7"/><path d="M6 7l1 13h10l1-13"/><path d="M10 11v6M14 11v6"/>'),
};

export interface OverflowMenuEntry {
  key: string;
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  reason?: string;
  danger?: boolean;
}

export interface OverflowMenuGroup {
  label: string;
  items: OverflowMenuEntry[];
}

const GroupLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <li
    className="px-[11px] pt-2.5 pb-[5px] font-bold uppercase text-text-2 list-none"
    style={{ fontSize: 9.5, letterSpacing: '.1em' }}
  >
    {children}
  </li>
);

const itemSx = (e: OverflowMenuEntry) => {
  let color = 'var(--text)';
  if (e.danger) color = 'var(--down)';
  else if (e.disabled) color = 'var(--muted)';
  let hoverBg = 'var(--surface-2)';
  if (e.disabled) hoverBg = 'transparent';
  else if (e.danger) hoverBg = 'var(--down-bg)';
  return {
    height: 38,
    borderRadius: '8px',
    px: '11px',
    mx: '6px',
    gap: '11px',
    fontSize: 13,
    fontFamily: 'inherit',
    color,
    cursor: e.disabled ? 'not-allowed' : 'pointer',
    '&:hover': { background: hoverBg },
  };
};

interface Props {
  groups: OverflowMenuGroup[];
  ariaLabel?: string;
  width?: number;
}

export const OverflowMenu: FC<Props> = ({ groups, ariaLabel = 'More actions', width = 266 }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const close = () => setAnchorEl(null);

  const visibleGroups = groups.filter((g) => g.items.length > 0);
  if (visibleGroups.length === 0) return null;

  return (
    <>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        className="inline-flex items-center justify-center w-7 h-[30px] rounded-lg text-text-2 hover:bg-surface-2 transition-colors shrink-0"
      >
        {KEBAB_GLYPH}
      </button>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              width,
              borderRadius: '12px',
              border: '1px solid var(--border)',
              boxShadow: 'var(--sh-2)',
              background: 'var(--surface)',
              backgroundImage: 'none',
            },
          },
        }}
        MenuListProps={{ sx: { py: '6px' } }}
      >
        {visibleGroups.map((g) => [
          g.label ? (
            <GroupLabel key={`gl-${g.label}`}>{g.label}</GroupLabel>
          ) : (
            <Box key={`div-${g.items[0].key}`} className="h-px mx-[11px] my-1.5 bg-border-token" component="li" />
          ),
          ...g.items.map((e) => (
            <MenuItem
              key={e.key}
              component={e.href ? 'a' : 'li'}
              {...(e.href ? { href: e.href, download: true } : {})}
              disableRipple
              onClick={() => {
                if (e.disabled) return;
                close();
                e.onClick?.();
              }}
              sx={itemSx(e)}
            >
              <span className={`inline-flex ${e.danger ? '' : 'text-text-2'}`} style={{ opacity: e.disabled ? 0.6 : 1 }}>
                {e.icon}
              </span>
              <span className="flex-1 min-w-0 truncate">{e.label}</span>
              {e.disabled && e.reason && (
                <span className="uppercase font-bold text-muted" style={{ fontSize: 10, letterSpacing: '.04em' }}>
                  {e.reason}
                </span>
              )}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
};

export default OverflowMenu;
